import {
  CheckableTagSelectItem,
  FlexBox,
  Select,
  TagSelectItem,
  TagSelectSuggestion,
} from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import { Tag } from 'lib-shared-types-api';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { tagsApi } from 'apis/tags';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { stringToHexColor } from 'utils/color/color';

import styles from './tagSelect.module.scss';

type TagSelectProps = {
  /** The selected tags. */
  selectedTags?: Tag[];
  /** Whether to use infinite scroll instead of manual pagination. */
  infiniteScroll?: boolean;
  /** Whether to include deactivated tags in the list. */
  includeDeactivated?: boolean;
  /** Whether to allow selecting deactivated tags. */
  allowSelectDeactivated?: boolean;
  /** Whether to allow creating new tags. */
  allowTagCreation?: boolean;
  /** Whether to allow multiple selection. */
  allowMultiple?: boolean;
  /** The placeholder text. */
  placeholder?: string;
  /** The callback for when tag selection is changed. */
  onChange?: (tags: readonly Tag[]) => void;
} & PropsWithClassName;

/** Represents the component for selecting one or more tags. */
export const TagSelect = ({
  className,
  selectedTags = [],
  infiniteScroll,
  includeDeactivated,
  allowSelectDeactivated,
  allowMultiple,
  allowTagCreation,
  placeholder,
  onChange,
}: TagSelectProps): JSX.Element => {
  const [currentSelectedTags, setCurrentSelectedTags] =
    useState<readonly Tag[]>(selectedTags);
  const [search, setSearch] = useState<{
    readonly term?: string;
    readonly debounceTime?: number;
  }>({});
  const {
    arg,
    asyncStatus,
    hasNextPage,
    hasPreviousPage,
    items: tags,
    changeArg,
    nextPage,
    previousPage,
  } = usePaginatedEndpoint({
    endpoint: tagsApi.endpoints.readTags,
    itemsSelector: ({ data: { tags } }) => tags,
    initialArg: {
      state: (includeDeactivated !== true && 'active') || undefined,
      sort: { key: 'name', direction: 'asc' },
    },
    infinite: infiniteScroll,
  });
  const [createTag] = tagsApi.useCreateTagMutation();
  const disabledTags = useMemo(
    () =>
      includeDeactivated && !allowSelectDeactivated
        ? tags.filter((tag) => tag.is_deactivated).map((tag) => tag.id)
        : [],
    [allowSelectDeactivated, includeDeactivated, tags],
  );
  const doesTagNameExist = useMemo(
    () =>
      tags.some((tag) => tag.name.toLowerCase() === search.term?.toLowerCase()),
    [search.term, tags],
  );
  const shouldShowSuggestion =
    allowTagCreation &&
    !doesTagNameExist &&
    (search.term?.trim().length ?? 0) > 1;

  /** Handles click on new tag suggestion. */
  const handleSuggest = useCallback(
    async (name: string) => {
      ampli.tagSelectCreateNewTagClicked({
        tagName: name,
        component: 'TagSelect',
      });

      const response = await createTag({ name, color: stringToHexColor(name) });

      if (isResponseWithData(response)) {
        setCurrentSelectedTags([...currentSelectedTags, response.data.data]);
      }
    },
    [createTag, currentSelectedTags],
  );

  /** Handles searching the tags. */
  const handleSearch = useCallback((value: string) => {
    setSearch({ term: value.trim() || undefined });
  }, []);

  // Changes the arg when search term changes.
  useEffect(() => {
    if (search.term !== arg?.searchTerm) {
      changeArg(
        {
          searchTerm: search.term,
          offset: undefined,
          limit: undefined,
        },
        { debounceTime: search?.debounceTime ?? 300 },
      );
    }
    // Don't rely on arg as dependency because we change the arg inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeArg, search]);

  /** Handles the change of selection. */
  const handleChange = useCallback(
    (tag: Tag, checked: boolean): void => {
      setCurrentSelectedTags(
        checked
          ? [...currentSelectedTags, tag]
          : currentSelectedTags.filter(({ id }) => id !== tag.id),
      );
    },
    [currentSelectedTags],
  );

  // Dispatches the on change even when selected tags are updated.
  useEffect(() => {
    onChange?.(currentSelectedTags);
  }, [onChange, currentSelectedTags]);

  /** Handles toggling the dropdown of the select component. */
  const handleToggle = useCallback((isOpen: boolean) => {
    if (isOpen) {
      setSearch({ debounceTime: 0 });
    }
  }, []);

  /** Handles navigating to the next page. */
  const handleNextPage = useCallback((): Promise<void> => {
    return Promise.resolve(nextPage());
  }, [nextPage]);

  /** Handles navigating to the previous page. */
  const handlePreviousPage = useCallback((): Promise<void> => {
    return Promise.resolve(previousPage());
  }, [previousPage]);

  /** Takes search string and returns suggestion item content */
  const renderSuggestion = useCallback(
    (search) => (
      <FlexBox gap="300" alignItems="center">
        <div
          className={styles.tagColor}
          style={{ backgroundColor: stringToHexColor(search) }}
        />
        <div>Create &quot;{search}&quot; tag</div>
      </FlexBox>
    ),
    [],
  );

  return (
    <Select
      className={className}
      multiple={allowMultiple}
      searchable
      emptyMessage={search.term ? 'No tags found' : 'No tags available'}
      // have to re-cast because readonly type
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      items={tags as Tag[]}
      // have to re-cast because readonly type
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      selectedItems={currentSelectedTags as any}
      disabledItems={disabledTags}
      itemComponent={allowMultiple ? CheckableTagSelectItem : TagSelectItem}
      loading={asyncStatus === 'pending'}
      onSearch={handleSearch}
      onToggle={handleToggle}
      onChange={handleChange}
      pagination={{
        infinite: infiniteScroll,
        nextEnable: hasNextPage,
        previousEnable: hasPreviousPage,
        onNext: handleNextPage,
        onPrevious: handlePreviousPage,
      }}
      placeholder={
        placeholder ??
        (allowMultiple ? 'Select one or more tags' : 'Select a tag')
      }
      onSuggest={handleSuggest}
      suggestionComponent={
        shouldShowSuggestion ? TagSelectSuggestion : undefined
      }
      renderSuggestion={renderSuggestion}
    />
  );
};
